<template>
    <div class="blog-list">
        <!-- Header Controls -->
        <header class="blog-list__header">
            <div class="row align-items-center">
                <div class="col-auto ml-auto d-flex align-items-center">
                    <button type="button" class="blog-list__tab-btn mr-3"
                        :class="{ 'blog-list__tab-btn--active': isMyBlogsTabActive }"
                        @click="isPublicView = !isPublicView">
                        {{ isPublicView ? 'My Blogs' : 'Public Blogs' }}
                    </button>

                    <!-- Guest User Profile Hint -->
                    <template v-if="isGuestUser">
                        <img src="@/assets/images/edit_profile.png" class="img-fluid blog-list__create-img" width="28"
                            alt="Create blog" title="Create blog" />
                    </template>

                    <!-- Authenticated User Create Action -->
                    <template v-else-if="userInfo">
                        <button v-if="!hasCompleteProfile" type="button" class="blog-list__create-icon-btn"
                            title="Create blog" aria-label="Create blog" @click="userInfoAlertPopupOpenFun">
                            <i class="fa fa-pencil-square-o" aria-hidden="true"></i>
                        </button>

                        <router-link v-else :to="{ name: 'blogCreate' }" class="blog-list__create-icon-btn"
                            title="Create blog" aria-label="Create blog">
                            <i class="fa fa-pencil-square-o" aria-hidden="true"></i>
                        </router-link>
                    </template>
                </div>
            </div>
        </header>

        <!-- Blog Feed List -->
        <main class="blog-list__body">
            <template v-if="displayedBlogList.length > 0">
                <article v-for="item in displayedBlogList" :key="item.id" class="blog-list__card">
                    <h2 class="blog-list__title">
                        <router-link :to="{ name: 'blogDetail', params: { blogId: item.id } }">
                            {{ item.title }}
                        </router-link>
                    </h2>

                    <div class="blog-list__excerpt" v-html="item.data"></div>

                    <div class="blog-list__read-more">
                        <router-link :to="{ name: 'blogDetail', params: { blogId: item.id } }">
                            Read More
                        </router-link>
                    </div>

                    <!-- Actions: Edit & Delete -->
                    <ul v-if="canManageBlog(item)" class="blog-list__actions">
                        <!-- Edit: Only for the blog author -->
                        <li v-if="isAuthor(item)">
                            <router-link :to="{ name: 'blogUpdate', params: { updateId: item.id } }" title="Edit blog">
                                <i class="fa fa-pencil" aria-hidden="true"></i>
                            </router-link>
                        </li>

                        <!-- Delete: Visible if user is the author OR a superuser -->
                        <li v-if="canDeleteBlog(item)">
                            <button type="button" class="blog-list__action-btn" title="Delete blog"
                                aria-label="Delete blog" @click="openRemoveBlogPopup(item.id)">
                                <i class="fa fa-trash" aria-hidden="true"></i>
                            </button>
                        </li>
                    </ul>
                </article>
            </template>

            <!-- Empty State -->
            <div v-else class="blog-list__empty">
                <h5>{{ isPublicView ? 'No public blogs available.' : "Sorry, you haven't published any blogs yet." }}
                </h5>
            </div>
        </main>

        <!-- Delete Confirmation Modal -->
        <div v-if="blogRemovedPopup" class="blog-list__modal-backdrop">
            <div class="blog-list__modal">
                <h3 class="blog-list__modal-title">Delete Blog</h3>
                <p class="blog-list__modal-text">Are you sure you want to delete this blog post? This action cannot be
                    undone.
                </p>
                <div class="blog-list__modal-actions">
                    <button type="button" class="blog-list__modal-btn blog-list__modal-btn--cancel"
                        :disabled="isSubmittingDelete" @click="closeRemoveItemModalFun">
                        Cancel
                    </button>
                    <button type="button" class="blog-list__modal-btn blog-list__modal-btn--confirm"
                        :disabled="isSubmittingDelete" @click="removeModalItemConfirmFun">
                        {{ isSubmittingDelete ? 'Deleting...' : 'Delete' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Incomplete Profile Alert Popup -->
        <profile-update-alert-popup v-if="profileUpdateAlertPopup" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import toast from '@/services/toast';
import { bus } from '@/main';
import ProfileUpdateAlertPopup from '@/components/private/common/profile-update-alert-popup/index.vue';

export default {
    name: 'BlogList',
    components: {
        'profile-update-alert-popup': ProfileUpdateAlertPopup
    },
    props: {
        userInfo: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isPublicView: true,
            blogRemovedPopup: false,
            removeBlogId: '',
            isSubmittingDelete: false,
            selfUserId: localStorage.getItem('LoginedUserID') || '',
            profileUpdateAlertPopup: false
        };
    },
    computed: {
        ...mapGetters(['publicBlogListGetters', 'blogListGetters']),

        isMyBlogsTabActive() {
            return !this.isPublicView;
        },

        isGuestUser() {
            return Boolean(this.userInfo && this.userInfo.guest);
        },

        isSuperUser() {
            return Boolean(this.userInfo && this.userInfo.is_superuser);
        },

        hasCompleteProfile() {
            return Boolean(
                this.userInfo &&
                this.userInfo.profile &&
                this.userInfo.profile.area_of_work &&
                this.userInfo.profile.bio
            );
        },

        personalBlogs() {
            const state = this.blogListGetters;
            return (state && state.data && Array.isArray(state.data.results)) ? state.data.results : [];
        },

        publicBlogs() {
            const state = this.publicBlogListGetters;
            return (state && state.data && Array.isArray(state.data.results)) ? state.data.results : [];
        },

        displayedBlogList() {
            return this.isPublicView ? this.publicBlogs : this.personalBlogs;
        }
    },
    mounted() {
        this.refreshBlogFeeds();
        bus.$on('profileUpdateAlertPopupBus', (state) => {
            this.profileUpdateAlertPopup = state;
        });
    },
    beforeDestroy() {
        bus.$off('profileUpdateAlertPopupBus');
        document.body.classList.remove('body_right_scroll_remove');
    },
    methods: {
        isAuthor(item) {
            return Boolean(item.user && String(item.user.id) === String(this.selfUserId));
        },
        canDeleteBlog(item) {
            return this.isAuthor(item) || this.isSuperUser;
        },
        canManageBlog(item) {
            return this.isAuthor(item) || this.canDeleteBlog(item);
        },

        userInfoAlertPopupOpenFun() {
            document.body.classList.add('body_right_scroll_remove');
            this.profileUpdateAlertPopup = true;
        },

        async refreshBlogFeeds() {
            try {
                await Promise.all([
                    this.$store.dispatch('getBlogListAction'),
                    this.$store.dispatch('getPublicBlogListAction')
                ]);
            } catch (err) {
                console.error('Failed to retrieve blog feeds:', err);
            }
        },

        openRemoveBlogPopup(id) {
            this.removeBlogId = id;
            this.blogRemovedPopup = true;
            document.body.classList.add('body_right_scroll_remove');
        },

        closeRemoveItemModalFun() {
            this.blogRemovedPopup = false;
            this.removeBlogId = '';
            document.body.classList.remove('body_right_scroll_remove');
        },

        async removeModalItemConfirmFun() {
            if (!this.removeBlogId) return;

            this.isSubmittingDelete = true;
            try {
                const res = await this.$store.dispatch('removeBlogDetailsAction', {
                    id: this.removeBlogId
                });

                const successMsg = (res && res.data && res.data.msg) || 'Blog deleted successfully';
                toast.success(successMsg);

                this.closeRemoveItemModalFun();
                await this.refreshBlogFeeds();
            } catch (err) {
                const errorMsg =
                    (err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) ||
                    (err.data && err.data.user_msg) ||
                    'Failed to remove blog';
                toast.error(errorMsg);
            } finally {
                this.isSubmittingDelete = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
$font-body: 'georgiaregular', Georgia, serif;
$font-title: 'the_bold_fontbold', -apple-system, BlinkMacSystemFont, sans-serif;
$font-button: 'MyriadProRegular', 'Lato', sans-serif;

$theme-gold: #ffd400;
$theme-gold-light: #fffcc4;
$accent-blue: #87cefa;
$color-text: #ffffff;
$color-border: rgba(255, 255, 255, 0.05);
$bg-card: rgba(255, 255, 255, 0.1);
$bg-card-alt: rgba(158, 182, 3, 0.15);

.blog-list {
    width: 100%;
    position: relative;

    &__header {
        border-bottom: 1px solid $color-border;
        padding: 10px 15px;
    }

    &__tab-btn {
        cursor: pointer;
        background: rgba(255, 255, 255, 0.12);
        color: $color-text;
        border: none;
        border-radius: 4px;
        font-family: $font-button;
        font-size: 15px;
        padding: 6px 14px;
        transition: background 0.2s ease, color 0.2s ease;

        &:hover,
        &--active {
            background: $accent-blue;
            color: #000000;
        }
    }

    &__create-icon-btn {
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        display: inline-flex;
        align-items: center;

        .fa {
            font-size: 26px;
            color: $color-text;
            transition: color 0.2s ease;

            &:hover {
                color: $theme-gold;
            }
        }
    }

    &__body {
        padding: 15px;
        height: calc(100dvh - 250px);
        overflow-y: auto;
        font-family: $font-body;

        &::-webkit-scrollbar {
            width: 6px;
            background: #000000;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #333333;
            border-radius: 4px;
        }
    }

    &__card {
        background: $bg-card;
        padding: 16px;
        border-radius: 6px;
        margin-bottom: 12px;
        position: relative;
        transition: background 0.2s ease;

        &:nth-child(2n) {
            background: $bg-card-alt;
        }

        &:hover .blog-list__actions {
            display: flex;
        }
    }

    &__title {
        font-size: 1.25rem;
        margin-bottom: 10px;
        line-height: 1.4;
        letter-spacing: 0.5px;

        a {
            color: $theme-gold-light;
            text-decoration: none;
            font-family: $font-title;
            transition: color 0.2s ease;

            &:hover {
                color: $accent-blue;
            }
        }
    }

    &__excerpt {
        font-size: 0.95rem;
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.85);
        margin-bottom: 10px;
        max-height: 120px;
        overflow: hidden;
    }

    &__read-more a {
        color: $accent-blue;
        text-decoration: underline;
        font-size: 0.9rem;
        font-weight: 500;

        &:hover {
            color: $theme-gold;
        }
    }

    &__actions {
        margin: 0;
        padding: 0;
        list-style: none;
        position: absolute;
        bottom: 12px;
        right: 16px;
        display: none;
        align-items: center;
        gap: 12px;

        a,
        .blog-list__action-btn {
            background: transparent;
            border: none;
            padding: 0;
            cursor: pointer;
            color: $color-text;
            font-size: 1.1rem;
            transition: color 0.2s ease;

            &:hover {
                color: $theme-gold;
            }
        }
    }

    &__empty {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        color: rgba(255, 255, 255, 0.6);

        h5 {
            font-size: 1.1rem;
            margin: 0;
        }
    }

    // Delete Confirmation Modal
    &__modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.85);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 15px;
    }

    &__modal {
        background: #111111;
        border: 1px solid rgba(255, 212, 0, 0.3);
        box-shadow: 0 0 16px rgba(255, 212, 0, 0.2);
        border-radius: 8px;
        width: 100%;
        max-width: 440px;
        padding: 24px;
        color: $color-text;
    }

    &__modal-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0 0 12px;
        color: $theme-gold;
    }

    &__modal-text {
        font-size: 0.95rem;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.8);
        margin: 0 0 24px;
    }

    &__modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
    }

    &__modal-btn {
        padding: 8px 16px;
        font-size: 16px;
        font-weight: normal;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: opacity 0.2s ease;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &--cancel {
            background: rgba(255, 255, 255, 0.1);
            color: $color-text;

            &:hover:not(:disabled) {
                background: rgba(255, 255, 255, 0.2);
            }
        }

        &--confirm {
            background: #dc3545;
            color: #ffffff;

            &:hover:not(:disabled) {
                background: #bd2130;
            }
        }
    }
}
</style>